/**
 * Phase 1.4 — Absatz-Fundament (ARKO, kein Cline).
 * Index-Map aus Extract-Text, cursorSelection für n8n, HITL-Vorschau (Phase 2: OOXML-Write).
 */

/** Stabile Absatz-Referenz für Webhook `documentContext` / `cursorSelection`. */
export interface ParagraphIndexEntry {
  paragraphId: string;
  index: number;
  hash: string;
  snippet: string;
}

export interface CursorSelection {
  paragraphId: string;
  index: number;
  snippet: string;
  source: 'user_reference' | 'editor' | 'inferred';
}

/** Phase 2-Vorbereitung: strukturiertes Edit-JSON (HITL, kein blindes OOXML in 1.4). */
export interface StructuredDocumentEdit {
  paragraphId: string;
  index: number;
  action: 'replace' | 'insert_after' | 'delete';
  originalSnippet: string;
  proposedText: string;
  rationale?: string;
}

const PARAGRAPH_SPLIT_RE = /\n\s*\n+/;

function simpleHash(text: string): string {
  let h = 0;
  const s = text.replace(/\s+/g, ' ').trim();
  for (let i = 0; i < s.length; i++) {
    h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  }
  return `p${Math.abs(h).toString(36)}`;
}

function extractBodyFromDocumentContext(documentContext: string): string {
  const marker = /Auszug \(UTF-8[^)]*\):\s*\n/i;
  const m = documentContext.match(marker);
  if (m && m.index != null) {
    return documentContext.slice(m.index + m[0].length).trim();
  }
  return documentContext.trim();
}

/** Baut Absatz-Index aus Volltext oder documentContext-Auszug. */
export function buildParagraphIndexMap(sourceText: string): ParagraphIndexEntry[] {
  const body = extractBodyFromDocumentContext(sourceText);
  if (!body) return [];

  const rawParts = body.split(PARAGRAPH_SPLIT_RE).map((p) => p.trim()).filter(Boolean);
  const parts = rawParts.length > 1 ? rawParts : body.split(/\n/).map((l) => l.trim()).filter(Boolean);

  return parts.slice(0, 200).map((text, i) => {
    const index = i + 1;
    const snippet = text.length > 160 ? `${text.slice(0, 157)}…` : text;
    return {
      paragraphId: `para-${index}`,
      index,
      hash: simpleHash(text),
      snippet,
    };
  });
}

/** „Absatz 3", „Paragraph 12", „§ 5" (Absatz-Kontext) im User-Text. */
const ABSATZ_REF_RE =
  /\b(?:absatz|paragraph|§)\s*(\d{1,3})\b/i;

export function resolveParagraphReference(
  userText: string,
  map: ParagraphIndexEntry[],
): ParagraphIndexEntry | null {
  const m = userText.match(ABSATZ_REF_RE);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  if (!Number.isFinite(n) || n < 1) return null;
  return map.find((p) => p.index === n) ?? null;
}

export function paragraphEntryToCursorSelection(
  entry: ParagraphIndexEntry,
  source: CursorSelection['source'] = 'user_reference',
): CursorSelection {
  return {
    paragraphId: entry.paragraphId,
    index: entry.index,
    snippet: entry.snippet,
    source,
  };
}

export function appendParagraphMapToDocumentContext(
  documentContext: string,
  map: ParagraphIndexEntry[],
): string {
  if (!map.length) return documentContext;
  const lines = map.slice(0, 40).map(
    (p) => `${p.index}. [${p.paragraphId}] ${p.snippet}`,
  );
  return [
    documentContext.trimEnd(),
    '',
    '=== Absatz-Index (Portal Phase 1.4) ===',
    ...lines,
    map.length > 40 ? `… (${map.length - 40} weitere Absätze)` : '',
  ].join('\n');
}

/** n8n/ARKO kann `structured_edit` als JSON-String oder Objekt liefern. */
export function parseStructuredDocumentEdit(raw: unknown): StructuredDocumentEdit | null {
  if (!raw) return null;
  let obj: Record<string, unknown>;
  if (typeof raw === 'string') {
    try {
      obj = JSON.parse(raw) as Record<string, unknown>;
    } catch {
      return null;
    }
  } else if (typeof raw === 'object' && raw !== null) {
    obj = raw as Record<string, unknown>;
  } else {
    return null;
  }

  const index = typeof obj.index === 'number' ? obj.index : parseInt(String(obj.index ?? ''), 10);
  const paragraphId = String(obj.paragraphId ?? obj.paragraph_id ?? `para-${index}`).trim();
  const action = obj.action;
  const proposedText = String(obj.proposedText ?? obj.proposed_text ?? obj.text ?? '').trim();
  const originalSnippet = String(obj.originalSnippet ?? obj.original_snippet ?? '').trim();

  if (!Number.isFinite(index) || index < 1 || !proposedText) return null;
  if (action !== 'replace' && action !== 'insert_after' && action !== 'delete') {
    return {
      paragraphId,
      index,
      action: 'replace',
      originalSnippet,
      proposedText,
      rationale: typeof obj.rationale === 'string' ? obj.rationale : undefined,
    };
  }

  return {
    paragraphId,
    index,
    action,
    originalSnippet,
    proposedText,
    rationale: typeof obj.rationale === 'string' ? obj.rationale : undefined,
  };
}
